/**
 * Toast (§13.4): noncritical confirmations only ("Copied", "Saved"). Errors and outcomes stay on screen in
 * their own components — never toast a loss, a win or a payment failure.
 */
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone?: 'neutral' | 'success';
  durationMs?: number;
}

interface ToastApi {
  toast: (t: Omit<ToastMessage, 'id'>) => void;
}

const ToastContext = createContext<ToastApi>({ toast: () => {} });

export function ToastProvider({
  children,
  label = 'Notifications',
}: {
  children: ReactNode;
  label?: string;
}) {
  const [items, setItems] = useState<ToastMessage[]>([]);
  const toast = useCallback((t: Omit<ToastMessage, 'id'>) => {
    setItems((list) => [...list.slice(-2), { ...t, id: Date.now() + list.length }]);
  }, []);
  const api = useMemo(() => ({ toast }), [toast]);
  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider label={label} duration={4000}>
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            className="wd-toast"
            data-tone={t.tone ?? 'neutral'}
            duration={t.durationMs}
            onOpenChange={(open) => {
              if (!open) setItems((list) => list.filter((x) => x.id !== t.id));
            }}
          >
            <div style={{ flex: 1 }}>
              <ToastPrimitive.Title style={{ fontWeight: 600 }}>{t.title}</ToastPrimitive.Title>
              {t.description ? (
                <ToastPrimitive.Description className="wd-muted">{t.description}</ToastPrimitive.Description>
              ) : null}
            </div>
            <ToastPrimitive.Close className="wd-btn wd-touch" data-variant="secondary" aria-label="Dismiss">
              <X size={18} aria-hidden="true" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="wd-toast-viewport" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
